import { useMemo } from 'react';
import type { DefaultTheme } from 'styled-components';
import { ThemeTypes, themes } from '../styles';
import { logger } from '../utils';
import { usePreferredColorScheme } from './usePreferredColorScheme';

/**
 * Gets the active theme based on user preferences and theme.
 */
export function useEffectiveThemeType(themeType: ThemeTypes): (typeof ThemeTypes)['dark' | 'light'] {
  const preferredType = usePreferredColorScheme();
  return themeType === ThemeTypes.auto ? preferredType : themeType;
}

export const usePreferredTheme = (themeName: string, themeType: ThemeTypes): DefaultTheme => {
  const preferredType = usePreferredColorScheme();
  const preferredThemeType = themeType === ThemeTypes.auto ? preferredType : themeType;
  return useThemeFromType(themeName, preferredThemeType);
};

export function useThemeFromType(themeName: string, themeType?: ThemeTypes) {
  return useMemo(() => {
    const preferredTheme =
      themeType != null && themeName != null
        ? themeType === ThemeTypes.light
          ? themeName.replace('Dark', 'Light')
          : themeName.replace('Light', 'Dark')
        : themeName;

    let theme: DefaultTheme;
    let resolvedThemeName: string;

    if (preferredTheme in themes) {
      theme = (themes as any)[preferredTheme];
      resolvedThemeName = preferredTheme;
    } else if (themeName in themes) {
      theme = (themes as any)[themeName];
      resolvedThemeName = themeName;
    } else {
      // Turns out, the theme is not in the themes object, lets default back to default theme and log error
      logger.error(new Error(`Theme: ${themeName} not found in themes object, defaulting to talos theme`));
      resolvedThemeName = themeType === ThemeTypes.light ? 'LightTheme' : 'DarkTheme';
      theme = (themes as any)[resolvedThemeName];
    }
    theme.themeName = resolvedThemeName;
    return theme;
  }, [themeName, themeType]);
}
